import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { LoaderFunctionArgs, useLoaderData, useNavigate } from "react-router-dom";
import {
    CooklangRecipeWithMetadata,
    cookRecipe,
    imageIdToUrl,
    loadRecipeRevisions,
    loadRecipes,
    Recipe as RecipeData,
} from "../data";
import Recipe, { ParsedRecipeWithMetadata } from "../components/Recipe";
import { Box, Paper, Stack } from "@mui/material";
import Helmet from "react-helmet";
import {
    RecipeDisplayControl,
    RecipeDisplayControlContext,
    useRecipeDisplayControlContext,
} from "../contexts/RecipeDisplayContext";
import { useCurrentHouseholdId } from "../contexts/AuthProvider";
import { normalize } from "../dates";
import Table, { ColumnDefinition } from "../components/base/Table";
import LastCooked from "../components/base/LastCooked";
import { replace } from "../utils/list";
import RecipeRevisions from "../components/RecipeRevisions";

function withImageUrls(
    recipeId: number,
    recipe: CooklangRecipeWithMetadata | undefined
): ParsedRecipeWithMetadata | undefined {
    if (!recipe) return undefined;

    const stepImages = new Map<number, string>();
    for (const step of recipe.images) {
        stepImages.set(step.step + 1, imageIdToUrl("medium", step.image_id)!);
    }

    return {
        id: recipeId,
        isOld: recipe.recipe.is_old !== 0,
        name: recipe.recipe.name,
        recipe: recipe.parsed,
        image: imageIdToUrl("large", recipe.recipe.image_id),
        stepImages,
    };
}

export interface RecipeInfo {
    recipeId: number;
    recipe: ParsedRecipeWithMetadata | undefined;
    servings?: number;
    fromHistory?: number;
}

export interface LoaderData {
    recipes: RecipeInfo[];
}

export type RecipeAndServings = [recipeId: number, servings?: number];

export async function singleRecipeLoader({ params, request }: LoaderFunctionArgs): Promise<LoaderData> {
    const recipeId = params.recipeId;
    if (!recipeId) {
        throw new Error("Missing recipeId");
    }
    const url = new URL(request.url);
    const parsedServings = parseInt(url.searchParams.get("servings") || "", 10);
    const servings = Number.isFinite(parsedServings) ? parsedServings : undefined;

    const fromHistory = parseInt(url.searchParams.get("from") || "", 10);

    const recipeIdNum = parseInt(recipeId, 10);
    const recipe = await cookRecipe(recipeIdNum);
    return {
        recipes: [
            {
                recipeId: recipeIdNum,
                recipe: withImageUrls(recipeIdNum, recipe),
                servings,
                fromHistory: Number.isFinite(fromHistory) ? fromHistory : undefined,
            },
        ],
    };
}

export async function multipleRecipeLoader(q: string): Promise<LoaderData> {
    try {
        const recipes = JSON.parse(q) as RecipeAndServings[];
        const parsedRecipes = await Promise.all(
            recipes.map(async ([recipeId, servings]) => {
                const recipe = await cookRecipe(recipeId);
                return {
                    recipeId,
                    recipe: withImageUrls(recipeId, recipe),
                    servings: Number.isFinite(servings) ? servings : undefined,
                };
            })
        );

        return {
            recipes: parsedRecipes,
        };
    } catch (e) {
        return { recipes: [] };
    }
}

export async function loader({ params, request }: LoaderFunctionArgs): Promise<LoaderData> {
    if (params.recipeId) return singleRecipeLoader({ params, request });

    const url = new URL(request.url);
    const q = url.searchParams.get("q");
    if (q) return multipleRecipeLoader(q);

    throw new Error("Missing recipeId");
}

interface EntryRecipe {
    uniqueId: number;
    type: "recipe";
    recipeId: number;
    recipe: ParsedRecipeWithMetadata | undefined;
    servings?: number;
    fromHistory?: number;
}

interface EntryPending {
    uniqueId: number;
    type: "pending";
}

interface EntryLoading {
    uniqueId: number;
    type: "loading";
    recipeId: number;
    servings?: number;
}

type Entry = EntryRecipe | EntryPending | EntryLoading;

function RecipeContent({ entry: { recipe, servings } }: { entry: EntryRecipe }) {
    let inner: ReactElement;
    if (recipe) {
        inner = <Recipe recipe={recipe} servings={servings} fractionAsNumber={false}></Recipe>;
    } else {
        inner = (
            <div>
                <h3>Recept hittades inte</h3>
                Verifera att länken är giltig.
            </div>
        );
    }

    return inner;
}

interface RevisionsContentProps {
    recipeId: number;
    currentRecipeId: number;
    onClose: () => void;
}

function RevisionsContent({ recipeId, currentRecipeId, onClose }: RevisionsContentProps) {
    const [revisions, setRevisions] = useState<RecipeData[] | null>(null);
    const navigate = useNavigate();
    const onSelect = useCallback(
        (revision: RecipeData) => {
            if (revision.id === recipeId) {
                navigate(`/recipe/${revision.id}`);
            } else {
                navigate(`/recipe/${revision.id}?from=${recipeId}`);
            }
        },
        [recipeId, navigate]
    );

    useEffect(() => {
        loadRecipeRevisions(recipeId).then(setRevisions);
    }, [recipeId]);

    if (!revisions) {
        return <div>Loading...</div>;
    }

    return (
        <RecipeRevisions
            currentRevisionId={currentRecipeId}
            revisions={revisions}
            onClose={onClose}
            onSelect={onSelect}
        ></RecipeRevisions>
    );
}

function SingleRecipeContent({ entry }: { entry: EntryRecipe }) {
    const controller = useRecipeDisplayControlContext();
    const [revisionsOpen, setRevisionsOpen] = useState(false);

    const myController = useMemo(() => {
        return {
            ...controller,
            showRevisions: () => {
                setRevisionsOpen(true);
            },
        };
    }, [controller, setRevisionsOpen]);

    return (
        <RecipeDisplayControlContext.Provider value={myController}>
            <Stack direction="row" spacing="15px" justifyContent="center" flexGrow={1}>
                <Paper
                    square={true}
                    elevation={8}
                    sx={theme => ({
                        backgroundColor: "white",
                        width: "100%",
                        paddingBottom: "30px",
                        ml: "auto",
                        mr: "auto",
                        paddingLeft: "5px",
                        paddingRight: "5px",
                        [theme.breakpoints.up("md")]: {
                            width: "800px",
                            paddingLeft: "30px",
                            paddingRight: "30px",
                        },
                    })}
                >
                    <RecipeContent entry={entry}></RecipeContent>
                </Paper>
                {revisionsOpen && (
                    <RevisionsContent
                        recipeId={entry.fromHistory ?? entry.recipeId}
                        currentRecipeId={entry.recipeId}
                        onClose={() => {
                            setRevisionsOpen(false);
                        }}
                    ></RevisionsContent>
                )}
            </Stack>
        </RecipeDisplayControlContext.Provider>
    );
}

interface SelectRecipeContentProps {
    onSelect: (recipe: RecipeData) => void;
}

function SelectRecipeContent({ onSelect }: SelectRecipeContentProps) {
    const currentHouseholdId = useCurrentHouseholdId();
    let [recipes, setRecipes] = useState<RecipeData[] | null>(null);

    useEffect(() => {
        loadRecipes(currentHouseholdId).then(recipes => {
            setRecipes(recipes.sort((a, b) => -(a.last_planned_date || "").localeCompare(b.last_planned_date || "")));
        });
    }, [currentHouseholdId]);

    if (!recipes) {
        return <div>Loading...</div>;
    }

    const today = normalize(new Date());

    const columns: ColumnDefinition<RecipeData>[] = [
        {
            name: "Namn",
            build: row => <span className="recipe-name">{row.name}</span>,
            sort: (a, b) => a.name.localeCompare(b.name),
        },
        {
            name: "Senast lagad",
            build: recipe => <LastCooked today={today} date={recipe.last_planned_date}></LastCooked>,
            sort: (a, b) => (a.last_planned_date || "").localeCompare(b.last_planned_date || ""),
        },
    ];

    return (
        <>
            <Box sx={{ height: "85vh" }}></Box>
            <Box
                sx={{
                    position: "absolute",
                    height: "85vh",
                    left: "30px",
                    right: "30px",
                    top: "15px",
                    bottom: "30px",
                    overflowY: "scroll",
                }}
            >
                <Table columns={columns} rows={recipes} rowSx={{ cursor: "pointer" }} onRowClick={onSelect}></Table>
            </Box>
        </>
    );
}

let uniqueIdCtr = 0;

function stateToUrlState(state: Entry[]): RecipeAndServings[] {
    let res: RecipeAndServings[] = [];

    for (const s of state) {
        if (s.type !== "pending") {
            res.push([s.recipeId, s.servings]);
        }
    }

    return res;
}

function isSameUrlState(a: RecipeAndServings[], b: RecipeAndServings[]): boolean {
    if (a.length !== b.length) return false;

    for (let index = 0; index < a.length; ++index) {
        if (a[index][0] !== b[index][0] || a[index][1] !== b[index][1]) return false;
    }

    return true;
}

function RecipeView() {
    const navigate = useNavigate();
    const { recipes } = useLoaderData() as LoaderData;
    const [state, setState] = useState<Entry[]>([]);
    const [oldState, setOldState] = useState<RecipeAndServings[]>([]);

    const addRecipe = useCallback(() => {
        setState(state => state.concat([{ type: "pending", uniqueId: ++uniqueIdCtr }]));
    }, [setState]);

    useEffect(() => {
        const newState = recipes.map<Entry>(r => ({ type: "recipe", uniqueId: ++uniqueIdCtr, ...r }));
        setState(newState);
        setOldState(stateToUrlState(newState));
    }, [recipes]);

    useEffect(() => {
        const newState = stateToUrlState(state);
        if (!isSameUrlState(newState, oldState)) {
            if (newState.length === 1) {
                navigate("/recipe/" + newState[0][0]);
            } else {
                navigate("/recipe/?q=" + encodeURIComponent(JSON.stringify(newState)));
            }
            setOldState(newState);
        }
    }, [state, oldState, navigate]);

    if (state.length === 1 && state[0].type === "recipe") {
        const controller: RecipeDisplayControl = {
            addRecipe,
        };
        return (
            <RecipeDisplayControlContext.Provider value={controller}>
                <Helmet>
                    <title>{state[0].recipe?.name || "Okänt recept"}</title>
                </Helmet>
                <Box
                    sx={{
                        backgroundColor: "rgb(223, 239, 255)",
                        pb: { md: "15px" },
                        minHeight: "100%",
                        flexGrow: "1",
                        display: "flex",
                    }}
                >
                    <SingleRecipeContent entry={state[0]}></SingleRecipeContent>
                </Box>
            </RecipeDisplayControlContext.Provider>
        );
    }

    const title = state
        .map(s => (s.type === "recipe" ? s.recipe?.name : undefined))
        .filter(n => n)
        .join(" & ");

    return (
        <Box sx={{ backgroundColor: "rgb(223, 239, 255)", pb: { md: "15px" }, minHeight: "100%", flexGrow: "1" }}>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Stack direction="row" spacing="15px" justifyContent="center">
                {state.map((s, index, arr) => {
                    const uniqueId = s.uniqueId;
                    const onSelectRecipe = async (recipe: RecipeData) => {
                        setState(replace(state, index, { type: "loading", uniqueId, recipeId: recipe.id }));
                        const recipeWithMetadata = await cookRecipe(recipe.id);
                        setState(state => {
                            const index = state.findIndex(s => s.uniqueId === uniqueId);
                            if (index === -1) return state;

                            return replace(state, index, {
                                type: "recipe",
                                uniqueId,
                                recipeId: recipe.id,
                                recipe: withImageUrls(recipe.id, recipeWithMetadata),
                            });
                        });
                    };

                    const controller: RecipeDisplayControl = {
                        addRecipe: arr.length >= 3 ? undefined : addRecipe,
                        removeRecipe: () => {
                            setState(state => state.filter(s => s.uniqueId !== uniqueId));
                        },
                    };
                    return (
                        <Paper
                            key={uniqueId}
                            square={true}
                            elevation={8}
                            sx={{
                                backgroundColor: "white",
                                paddingLeft: { md: "30px" },
                                paddingRight: { md: "30px" },
                                paddingBottom: "30px",
                                position: "relative",
                                maxWidth: "800px",
                                width: `${100 / arr.length}%`,
                                height: "fit-content",
                            }}
                        >
                            <RecipeDisplayControlContext.Provider value={controller}>
                                {s.type === "recipe" ? (
                                    <RecipeContent entry={s}></RecipeContent>
                                ) : (
                                    <SelectRecipeContent onSelect={onSelectRecipe}></SelectRecipeContent>
                                )}
                            </RecipeDisplayControlContext.Provider>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
}

export default RecipeView;
